import { Box, Button, Chip, SvgIcon, Typography } from '@mui/material';
import FileAckIcon from 'assets/icons/file.svg';
import FileSignIcon from 'assets/icons/file-sign.svg';
import {
  markSignatureDismissed,
  notifySignatureViewed,
  refreshPendingSignatureCount,
  subscribeSignatureDeclined,
  subscribeSignatureSigned,
  subscribeSignatureViewed,
  unmarkSignatureDismissed,
} from 'components/@home/@signatures/pendingSignatures';
import {
  SIGNATURE_STATUS_COLOR,
  documentLabel,
  isSignatureOpenable,
  mergeSignatureRows,
  signatureDate,
  signatureRowActions,
} from 'components/@home/@signatures/signatureRows';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import StatusChip from 'components/common/StatusChip';
import SearchField from 'components/controls/SearchField';
import qs from 'querystring';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Http from 'services/Http';
import acknowledgmentReadModal from 'store/app/ui/modals/acknowledgment/read/action';
import authEmployee from 'store/selectors/authEmployee';
import { I18n } from 'utils/i18n';
import renderDateTimeCell from 'utils/renderDateTimeCell';
import { useDebounceValue } from 'utils/useDebounceValue';
import useActions from 'utils/useActions';

const FILTERS = [
  ['pending', 'ProfileDrawer.signature filter pending'],
  ['signed', 'ProfileDrawer.signature filter signed'],
  ['all', 'ProfileDrawer.signature filter all'],
];

const ToolbarNone = () => null;

const NoSignatures = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
    <Typography color="text.secondary">
      {I18n.t('SettingsDrawer.No signatures in this view')}
    </Typography>
  </Box>
);

const MySignatures = () => {
  const employee = useSelector(authEmployee);
  const locale = useSelector(state => state.i18n.locale);
  const openRead = useActions(acknowledgmentReadModal.open);
  const [status, setStatus] = useState('pending');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebounceValue(searchInput, 500);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(0);
  const [messages, setMessages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const requestId = useRef(0);
  const signedNow = useRef(new Map());
  const seenOnServer = useRef(new Set());

  const filterKey = `${status}|${debouncedSearch}`;
  const [pageFilter, setPageFilter] = useState(filterKey);
  if (pageFilter !== filterKey) {
    setPageFilter(filterKey);
    setPage(0);
  }

  const load = useCallback(async () => {
    if (!perPage) return;
    const current = requestId.current + 1;
    requestId.current = current;
    setLoading(true);
    setLoadError('');
    try {
      const body = await Http.get(
        `/v1/signatures?${qs.stringify({
          status,
          page: page + 1,
          perPage,
          ...(debouncedSearch ? { search: debouncedSearch, searchBy: 'text' } : {}),
        })}`,
      );
      if (current !== requestId.current) return;
      const payload = body?.data?.messages ? body.data : body;
      const pageMessages = Array.isArray(payload?.messages) ? payload.messages : [];
      pageMessages.forEach(item => seenOnServer.current.add(String(item._id)));
      const next = mergeSignatureRows(
        status,
        pageMessages,
        signedNow.current,
        page,
        seenOnServer.current,
      );
      setMessages(next);
      setTotalCount(Math.max(Number(payload?.totalCount) || 0, next.length));
    } catch (error) {
      if (current !== requestId.current) return;
      setMessages([]);
      setTotalCount(0);
      setLoadError(error?.data?.error?.message || error?.message || '');
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, [debouncedSearch, page, perPage, status]);

  useEffect(() => {
    load();
  }, [load]);

  const applyViewed = useCallback(messageId => {
    setMessages(current =>
      current.map(item =>
        String(item._id) === String(messageId) && item.displayStatus === 'pending'
          ? { ...item, displayStatus: 'viewed' }
          : item,
      ),
    );
  }, []);

  useEffect(() => subscribeSignatureViewed(applyViewed), [applyViewed]);

  const applySigned = useCallback(
    messageId => {
      const id = String(messageId);
      setMessages(current => {
        const existing = current.find(item => String(item._id) === id);
        if (existing) {
          signedNow.current.set(id, { ...existing, displayStatus: 'signed', status: 'signed' });
        }
        const signedItem = signedNow.current.get(id);
        if (status === 'signed') {
          const rest = current.filter(item => String(item._id) !== id);
          return signedItem ? [signedItem, ...rest] : rest;
        }
        return signedItem ? [signedItem] : current.filter(item => String(item._id) !== id);
      });
      if (status !== 'signed') setStatus('signed');
      refreshPendingSignatureCount();
    },
    [status],
  );

  useEffect(() => subscribeSignatureSigned(applySigned), [applySigned]);

  const applyDeclined = useCallback(
    messageId => {
      setMessages(current =>
        status === 'pending'
          ? current.filter(item => String(item._id) !== String(messageId))
          : current.map(item =>
              String(item._id) === String(messageId)
                ? { ...item, displayStatus: 'declined' }
                : item,
            ),
      );
    },
    [status],
  );

  useEffect(() => subscribeSignatureDeclined(applyDeclined), [applyDeclined]);

  const markViewed = useCallback(messageId => {
    Http.post(`/v1/signatures/${messageId}/viewed`).catch(() => {});
    notifySignatureViewed(messageId);
  }, []);

  const decline = useCallback(
    async (event, messageId) => {
      event.stopPropagation();
      event.preventDefault();
      const previous = messages;
      const previousCount = totalCount;
      setMessages(current =>
        status === 'pending'
          ? current.filter(item => item._id !== messageId)
          : current.map(item =>
              item._id === messageId ? { ...item, displayStatus: 'declined' } : item,
            ),
      );
      if (status === 'pending') setTotalCount(count => Math.max(0, count - 1));
      try {
        await Http.post(`/v1/signatures/${messageId}/decline`);
        refreshPendingSignatureCount();
      } catch {
        setMessages(previous);
        setTotalCount(previousCount);
      }
    },
    [messages, status, totalCount],
  );

  const dismiss = useCallback(
    async (event, messageId) => {
      event.stopPropagation();
      event.preventDefault();
      const previous = messages;
      const previousCount = totalCount;
      markSignatureDismissed(messageId);
      setMessages(current => current.filter(item => item._id !== messageId));
      setTotalCount(count => Math.max(0, count - 1));
      try {
        await Http.post(`/v1/signatures/${messageId}/not-applicable`);
        refreshPendingSignatureCount();
      } catch {
        unmarkSignatureDismissed(messageId);
        setMessages(previous);
        setTotalCount(previousCount);
      }
    },
    [messages, totalCount],
  );

  const NoRows = useCallback(() => (loadError ? null : <NoSignatures />), [loadError]);

  const openRow = useCallback(
    message => {
      if (!isSignatureOpenable(message)) return;
      if (message.displayStatus === 'pending') markViewed(message._id);
      openRead({ ...message, acknowledges: message.acknowledges || [] }, message.channel, employee);
    },
    [employee, markViewed, openRead],
  );

  const columns = useMemo(() => {
    // I18n.t reads the active locale from the store, so the memo must depend on it.
    void locale;
    const showActions = messages.some(row => {
      const actions = signatureRowActions(row);
      return actions.decline || actions.dismiss;
    });
    const next = [
      {
        field: 'document',
        flex: 1.6,
        minWidth: 280,
        sortable: false,
        filterable: false,
        headerName: I18n.t('SignTracking.Table.document'),
        valueGetter: (_value, row) => documentLabel(row),
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, width: '100%' }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: 'primary.ultraLight',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto',
              }}
            >
              <SvgIcon
                sx={{ fontSize: 18, color: 'primary.main', '& path': { fill: 'currentColor' } }}
              >
                {row.digitalSignature ? <FileSignIcon /> : <FileAckIcon />}
              </SvgIcon>
            </Box>
            <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
              {documentLabel(row)}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'displayStatus',
        flex: 0.7,
        minWidth: 160,
        sortable: false,
        filterable: false,
        headerName: I18n.t('SignTracking.Table.status'),
        renderCell: ({ row }) => (
          <StatusChip
            color={SIGNATURE_STATUS_COLOR[row.displayStatus] || 'default'}
            label={
              row.displayStatus ? I18n.t(`ProfileDrawer.signature status ${row.displayStatus}`) : ''
            }
          />
        ),
      },
      {
        field: 'receivedAt',
        flex: 0.8,
        minWidth: 180,
        sortable: false,
        filterable: false,
        headerName: I18n.t('Fields.Date'),
        valueGetter: (_value, row) => signatureDate(row, employee),
        renderCell: renderDateTimeCell,
      },
      {
        field: 'channelName',
        flex: 0.8,
        minWidth: 160,
        sortable: false,
        filterable: false,
        headerName: I18n.t('SignTracking.Table.channel'),
        renderCell: ({ value }) => (
          <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>
            {value || ''}
          </Typography>
        ),
      },
    ];
    if (showActions) {
      next.push({
        field: 'actions',
        width: 280,
        sortable: false,
        filterable: false,
        headerName: '',
        renderCell: ({ row }) => {
          const actions = signatureRowActions(row);
          if (!actions.decline && !actions.dismiss) return null;
          return (
            <Box sx={{ display: 'flex', gap: 1 }} onClick={event => event.stopPropagation()}>
              {actions.decline ? (
                <Button variant="outlined" size="small" onClick={event => decline(event, row._id)}>
                  {I18n.t('AcknowledgementMessage.Decline')}
                </Button>
              ) : null}
              {actions.dismiss ? (
                <Button variant="outlined" size="small" onClick={event => dismiss(event, row._id)}>
                  {I18n.t('AcknowledgementMessage.Does not apply')}
                </Button>
              ) : null}
            </Box>
          );
        },
      });
    }
    return next;
  }, [decline, dismiss, employee, locale, messages]);

  return (
    <Box
      sx={{
        p: 2,
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="h3">{I18n.t('SettingsDrawer.My signatures')}</Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          rowGap: 1.5,
          mt: 2,
          mb: 1,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          {FILTERS.map(([value, label]) => (
            <Chip
              key={value}
              clickable
              variant={status === value ? 'filled' : 'outlined'}
              color={status === value ? 'primary' : 'default'}
              label={I18n.t(label)}
              onClick={() => setStatus(value)}
              sx={{ borderRadius: 999, fontWeight: 600 }}
            />
          ))}
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 280, md: 320 }, flexShrink: 0 }}>
          <SearchField
            fullWidth
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            placeholder={I18n.t('SignTracking.SearchPlaceholder')}
          />
        </Box>
      </Box>
      {loadError ? (
        <Typography color="error" sx={{ mb: 1 }}>
          {loadError}
        </Typography>
      ) : null}
      <OmmnioDataGrid
        loading={!perPage || loading}
        sx={{
          flex: 1,
          '& .MuiDataGrid-row': { cursor: 'pointer' },
          '& .MuiDataGrid-row.signature-closed': { cursor: 'default' },
        }}
        slots={{
          toolbar: ToolbarNone,
          noRowsOverlay: NoRows,
        }}
        columns={columns}
        rows={messages}
        getRowId={row => row._id}
        rowCount={totalCount}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        page={page}
        pageSize={perPage}
        onPageChange={setPage}
        onPageSizeChange={setPerPage}
        defaultSortModel={[{ field: 'createdAt', sort: 'desc' }]}
        disableEvenOdd
        disableRowSelectionOnClick
        onCellClick={cell => {
          if (cell.field === 'actions') return;
          openRow(cell.row);
        }}
        getRowClassName={({ row }) => (isSignatureOpenable(row) ? '' : 'signature-closed')}
      />
    </Box>
  );
};

export default MySignatures;
